import React from 'react';
import { Input } from '../../../../shared/components/Input';
import { Select } from '../../../../shared/components/Select';
import { Checkbox } from '../../../../shared/components/Checkbox';
import {
  ModuleDef, ModuleSourceRow, ARRIVAL_TYPES, createSourceRow,
} from './moduleDefs';
import {
  PERCENT_PER_FRACTION,
  formatAutoExtractRate,
  fractionToPercent,
  percentToFraction,
} from './materialCostModel';

interface Props {
  def: ModuleDef;
  sources: ModuleSourceRow[];
  onChange: (sources: ModuleSourceRow[]) => void;
}

// Global Material Traceability defaults shown as placeholders. A per-mill value
// is only sent when the rep types one; otherwise the backend uses its global
// config default for that mill.
const DEFAULT_REPORTS_PER_PIPE = 19;
const DEFAULT_MINUTES_PER_EXCEPTION = 4;

// Plain-language hints shown on each onboarding column.
const TOOLTIP_PIPES = "How many pipes this mill supplies. Drives this mill's tracking cost.";
const TOOLTIP_INSPECTIONS =
  "How many inspections this vendor covers. Summed across vendors to set the total inspection volume.";
const TOOLTIP_REPORT_TYPES =
  "How many distinct report types this vendor delivers (RT, UT, PAUT, and so on). Drives the onboarding build hours. Leave blank to count the data types ticked below.";
const TOOLTIP_REPORTS_PER_PIPE =
  'How many test reports this mill provides per pipe. More reports means more to process per pipe.';
const TOOLTIP_MINUTES_PER_EXCEPTION =
  'Minutes to fix one document that fails auto-extraction. Higher raises the per-pipe cost.';
const TOOLTIP_AUTO_EXTRACT =
  "Share of this mill's documents read automatically. Lower means more manual exceptions, so a higher per-pipe cost. Defaults to the format's rate.";
const TOOLTIP_AUTO_EXTRACT_RECORD =
  "Share of this source's documents read automatically. Lower means more manual exceptions, so a higher per-record cost. Leave blank to use the module default.";
const DEFAULT_AUTO_EXTRACT_PCT = 98;
const TOOLTIP_SOURCE_FORMAT =
  'How this source arrives (API, structured file, scanned PDF, and so on). Sets the onboarding effort and the default auto-extract rate.';
const TOOLTIP_NRE_OVERRIDE =
  'Set the one-time onboarding charge for this source by hand. Leave blank to price it automatically.';
const TOOLTIP_CHARGE_NRE =
  'Uncheck if this source was already onboarded on a past project, to skip its one-time onboarding fee.';
const CHARGE_NRE_LABEL = 'Charge NRE';
const TOOLTIP_CAPTURE_MODE =
  'BYOD: the contractor brings its own files (pick the data format). Bolt-on: we supply the CRCE machines (enter how many), priced like a welding deal.';
const TOOLTIP_BOLT_ON =
  'CRCE machines we supply for this contractor, priced like a welding deal (hardware + software + internet/cloud + services).';
const CAPTURE_MODES = [
  { value: 'byod', label: 'BYOD (they bring data)' },
  { value: 'bolton', label: 'Bolt-on (our hardware)' },
];

const toNumber = (value: string): number | undefined =>
  value === '' ? undefined : Math.max(0, Number(value) || 0);

export const ModuleSourceTable: React.FC<Props> = ({ def, sources, onChange }) => {
  const patch = (id: string, change: Partial<ModuleSourceRow>) =>
    onChange(sources.map((s) => (s.id === id ? { ...s, ...change } : s)));

  const addRow = () =>
    onChange([...sources, createSourceRow(`${def.key}-${sources.length}-${Date.now()}`, def.hasCapturePath)]);

  const removeRow = (id: string) => onChange(sources.filter((s) => s.id !== id));

  const showPipeCount = def.key === 'materialTraceability';
  // NDT and Coating both carry a per-source record count that sums to the module
  // volume and is priced by the source's data format.
  const showInspectionCount = def.key === 'ndt' || def.key === 'coating';
  const countLabel = def.key === 'coating' ? 'Joints' : 'Inspections';

  const toggleType = (row: ModuleSourceRow, type: string) =>
    patch(row.id, {
      dataTypes: row.dataTypes.includes(type)
        ? row.dataTypes.filter((t) => t !== type)
        : [...row.dataTypes, type],
    });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-medium text-blueprint-blue">{def.rowNoun} onboarding</h3>
        <button
          type="button"
          onClick={addRow}
          className="px-3 py-1.5 text-sm font-medium text-white bg-readable-red hover:bg-hero-red-70 rounded-lg"
        >
          + Add {def.rowNoun.toLowerCase()}
        </button>
      </div>

      {sources.length === 0 ? (
        <p className="text-sm text-heritage-grey">No {def.rowNoun.toLowerCase()} rows yet. Add at least one to price onboarding.</p>
      ) : (
        <div className="space-y-4">
          {sources.map((row) => {
            // Coating contractors are BYOD by default; a bolt-on contractor hides
            // the data-format / joint fields and shows the machine inputs instead.
            const coatingBoltOn = def.key === 'coating' && row.captureMode === 'bolton';
            return (
            <div key={row.id} className="rounded-xl border border-[#E2E5EA] p-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-56">
                  <Input
                    label={def.rowNoun}
                    tooltip={`Name of this ${def.rowNoun.toLowerCase()}. Labels the onboarding row; it does not change the price.`}
                    value={row.name}
                    onChange={(e) => patch(row.id, { name: e.target.value })}
                    placeholder={def.rowNoun}
                  />
                </div>
                {def.key === 'coating' && (
                  <div className="w-52">
                    <Select
                      label="Capture mode"
                      tooltip={TOOLTIP_CAPTURE_MODE}
                      value={row.captureMode ?? 'byod'}
                      options={CAPTURE_MODES}
                      onChange={(v) => patch(row.id, { captureMode: v })}
                    />
                  </div>
                )}
                {!coatingBoltOn && (
                  <div className="w-48">
                    <Select
                      label={def.key === 'coating' ? 'Data format' : 'Source format'}
                      tooltip={TOOLTIP_SOURCE_FORMAT}
                      value={row.sourceArrivalType}
                      options={ARRIVAL_TYPES}
                      onChange={(v) => patch(row.id, { sourceArrivalType: v })}
                    />
                  </div>
                )}
                {showInspectionCount && !coatingBoltOn && (
                  <div className="w-28">
                    <Input
                      label={countLabel}
                      tooltip={TOOLTIP_INSPECTIONS}
                      type="number"
                      min={0}
                      value={row.pipeCount ?? ''}
                      onChange={(e) => patch(row.id, { pipeCount: toNumber(e.target.value) })}
                      placeholder="0"
                    />
                  </div>
                )}
                {showInspectionCount && !coatingBoltOn && (
                  <div className="w-28">
                    <Input
                      label="Report types"
                      tooltip={TOOLTIP_REPORT_TYPES}
                      type="number"
                      min={0}
                      value={row.reportsPerPipe ?? ''}
                      onChange={(e) => patch(row.id, { reportsPerPipe: toNumber(e.target.value) })}
                      placeholder={row.dataTypes?.length ? String(row.dataTypes.length) : 'auto'}
                    />
                  </div>
                )}
                {showInspectionCount && !coatingBoltOn && (
                  <div className="w-28">
                    <Input
                      label="Auto-extract %"
                      tooltip={TOOLTIP_AUTO_EXTRACT_RECORD}
                      type="number"
                      min={0}
                      max={PERCENT_PER_FRACTION}
                      value={fractionToPercent(row.autoExtractRate) ?? ''}
                      onChange={(e) =>
                        patch(row.id, { autoExtractRate: percentToFraction(e.target.value) })
                      }
                      placeholder={String(DEFAULT_AUTO_EXTRACT_PCT)}
                    />
                  </div>
                )}
                {showInspectionCount && !coatingBoltOn && (
                  <div className="w-28">
                    <Input
                      label="Min/exception"
                      tooltip={TOOLTIP_MINUTES_PER_EXCEPTION}
                      type="number"
                      min={0}
                      value={row.minutesPerException ?? ''}
                      onChange={(e) => patch(row.id, { minutesPerException: toNumber(e.target.value) })}
                      placeholder={String(DEFAULT_MINUTES_PER_EXCEPTION)}
                    />
                  </div>
                )}
                {showPipeCount && (
                  <div className="w-28">
                    <Input
                      label="Pipes"
                      tooltip={TOOLTIP_PIPES}
                      type="number"
                      min={0}
                      value={row.pipeCount ?? ''}
                      onChange={(e) => patch(row.id, { pipeCount: toNumber(e.target.value) })}
                      placeholder="0"
                    />
                  </div>
                )}
                {showPipeCount && (
                  <div className="w-28">
                    <Input
                      label="Reports/pipe"
                      tooltip={TOOLTIP_REPORTS_PER_PIPE}
                      type="number"
                      min={0}
                      value={row.reportsPerPipe ?? ''}
                      onChange={(e) => patch(row.id, { reportsPerPipe: toNumber(e.target.value) })}
                      placeholder={String(DEFAULT_REPORTS_PER_PIPE)}
                    />
                  </div>
                )}
                {showPipeCount && (
                  <div className="w-28">
                    <Input
                      label="Auto-extract %"
                      tooltip={TOOLTIP_AUTO_EXTRACT}
                      type="number"
                      min={0}
                      max={PERCENT_PER_FRACTION}
                      value={fractionToPercent(row.autoExtractRate) ?? ''}
                      onChange={(e) =>
                        patch(row.id, { autoExtractRate: percentToFraction(e.target.value) })
                      }
                      placeholder={String(
                        fractionToPercent(formatAutoExtractRate(row.sourceArrivalType))
                      )}
                    />
                  </div>
                )}
                {showPipeCount && (
                  <div className="w-28">
                    <Input
                      label="Min/exception"
                      tooltip={TOOLTIP_MINUTES_PER_EXCEPTION}
                      type="number"
                      min={0}
                      value={row.minutesPerException ?? ''}
                      onChange={(e) => patch(row.id, { minutesPerException: toNumber(e.target.value) })}
                      placeholder={String(DEFAULT_MINUTES_PER_EXCEPTION)}
                    />
                  </div>
                )}
                {!coatingBoltOn && (
                  <div className="w-32">
                    <Input
                      label="NRE override"
                      tooltip={TOOLTIP_NRE_OVERRIDE}
                      type="number"
                      min={0}
                      value={row.nreOverride ?? ''}
                      onChange={(e) => patch(row.id, { nreOverride: toNumber(e.target.value) })}
                      placeholder="auto"
                    />
                  </div>
                )}
                {!coatingBoltOn && (
                  <div className="pb-2">
                    <Checkbox
                      id={`charge-nre-${row.id}`}
                      label={CHARGE_NRE_LABEL}
                      tooltip={TOOLTIP_CHARGE_NRE}
                      checked={row.chargeNre !== false}
                      onChange={(e) => patch(row.id, { chargeNre: e.target.checked })}
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="ml-auto text-sm text-readable-red hover:text-readable-red"
                >
                  Remove
                </button>
              </div>

              {coatingBoltOn && (
                <div className="flex flex-wrap items-end gap-3 rounded-md bg-page-bg p-3">
                  <span className="self-center text-xs font-medium text-heritage-grey" title={TOOLTIP_BOLT_ON}>
                    Bolt-on hardware:
                  </span>
                  <div className="w-28">
                    <Input
                      id={`bolton-machines-${row.id}`}
                      label="Machines"
                      tooltip={TOOLTIP_BOLT_ON}
                      type="number"
                      min={0}
                      value={row.boltOnMachineCount ?? ''}
                      onChange={(e) => patch(row.id, { boltOnMachineCount: toNumber(e.target.value) })}
                      placeholder="0"
                    />
                  </div>
                  <div className="w-28">
                    <Input
                      id={`bolton-support-${row.id}`}
                      label="Support hrs / mo"
                      type="number"
                      min={0}
                      value={row.boltOnSupportHours ?? ''}
                      onChange={(e) => patch(row.id, { boltOnSupportHours: toNumber(e.target.value) })}
                      placeholder="0"
                    />
                  </div>
                  <div className="w-28">
                    <Input
                      id={`bolton-training-${row.id}`}
                      label="Training people"
                      type="number"
                      min={0}
                      value={row.boltOnTrainingPeople ?? ''}
                      onChange={(e) => patch(row.id, { boltOnTrainingPeople: toNumber(e.target.value) })}
                      placeholder="0"
                    />
                  </div>
                  <div className="pb-2">
                    <Checkbox
                      id={`bolton-ship-${row.id}`}
                      label="Ship machines"
                      checked={Boolean(row.boltOnMobilize)}
                      onChange={(e) => patch(row.id, { boltOnMobilize: e.target.checked || undefined })}
                    />
                  </div>
                </div>
              )}

              {!coatingBoltOn && (
                <div>
                  <p className="text-xs font-medium text-heritage-grey mb-1.5">Data types ({row.dataTypes.length} selected)</p>
                  <div className="flex flex-wrap gap-2">
                    {def.dataTypes.map((type) => {
                      const active = row.dataTypes.includes(type);
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => toggleType(row, type)}
                          className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                            active
                              ? 'bg-hero-red-10 border-hero-red-20 text-readable-red'
                              : 'bg-white border-[#E2E5EA] text-text-grey hover:border-heritage-grey'
                          }`}
                        >
                          {type}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
